import React, { Component } from 'react';
import { get } from 'lodash';
import styled from 'styled-components';
import { connect } from 'react-redux';
import fastMemoize from 'fast-memoize';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchRealValue } from 'services/realValue/duck';
import { setTimeRangeByKey } from 'services/config/duck';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { NoDataTile } from 'components/molecules/ErrorTile';

// Components and Containers
import { Grid } from 'react-bootstrap';
import StyledSelect from 'components/atoms/inputs/Select';
import RenderingTile from 'components/molecules/RenderingTile';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';

import ChannelTitle from 'components/atoms/layout/ChannelTitle';

import {
    HelpBody,
    HelpTitle,
} from 'components/atoms/callout/Help';


const Select300 = styled(StyledSelect)`
    width: 300px;
`;

const getCampaignAdgroups = workorderData => Object.keys(workorderData).reduce((acc, id) => {
    // Get the adgroup dims from the frist item of the adgroup id data list.
    const adgroupRecord = workorderData[id].items[0];
    const adgroupName = adgroupRecord.adgroupname;

    const campaignId = adgroupRecord.campaign_id;
    const campaignName = adgroupRecord.campaign_name || campaignId;
    const campaign = acc[campaignId] || {
        id: campaignId,
        name: campaignName,
        adgroups: {}
    };

    campaign.adgroups[id] = adgroupName;

    acc[campaignId] = campaign;
    return acc;
}, {});
const memoizedGetCampaignAdgroups = fastMemoize(getCampaignAdgroups);

const RvDropdown = ({ labelName, onChange, selectedId, children }) => (
    <div style={{ marginBottom: '22px' }}>
        <label style={{ margin: '0 5px 0 5px' }}>{labelName}</label>
        <Select300 value={selectedId} onChange={onChange}>
            {children}
        </Select300>
    </div>
);

class LabsRealValueContainer extends Component {
    constructor(props) {
        super(props);
        const rvData = get(props.realValue, 'data') || {};

        this.state = {
            entityId: null,
            entityLookup: {},
            selectedAdgroupId: null,
            selectedCampaignId: null,
            campaigns: memoizedGetCampaignAdgroups(rvData)
        };
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.entityId || props.match.params.entityId, 10);

        const newState = { ...state };

        let touch = false;
        if (entityId !== state.entityId) {
            newState.entityId = entityId;
            touch = true;
        }

        const rvData = get(props.realValue, 'data') || {};

        const campaigns = memoizedGetCampaignAdgroups(rvData);
        if (campaigns !== state.campaigns) {
            newState.campaigns = campaigns;
            touch = true;
        }

        if (touch) {
            return newState;
        }

        return null;
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    createPayload(entityId) {
        return {
            transactionId: `entity-${entityId}-lab-realvalue`,
            entityId
        };
    }

    makeRequestsForData() {
        const entityId = this.state.entityId;
        const payload = this.createPayload(entityId);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchRealValue(payload);
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.state.entityId;
        const isTimeRangeChanged = prevProps.selectedTimeRange !== this.props.selectedTimeRange;

        if (isTimeRangeChanged || isEntityChanged) {
            this.makeRequestsForData();
        }
    }

    getCurrentEntityId() {
        return this.state.entityId;
    }

    changeAdgroup = (event) => {
        this.setState({ selectedAdgroupId: event.target.value });
    }

    changeCampaign = (event) => {
        this.setState({
            selectedCampaignId: event.target.value,
            selectedAdgroupId: null
        });
    }

    render() {
        const title = 'Real Value Attribution';
        const hasData = this.props.realValue.hasData;

        const {
            backfillBuckets,
        } = this.props;

        if (!hasData) {
            return (
                <Grid fluid={true}>
                    <ChannelTitle>{title}</ChannelTitle>
                    {hasData === false ? (
                        <NoDataTile height='400px'>
                            <h3>No {title} data for that date range</h3>
                        </NoDataTile>
                    ) : (
                        <RenderingTile height='400px' show={hasData} />
                    )}
                </Grid>
            );
        }

        const workorderData = get(this.props.realValue, 'data', {});

        const campaignIds = Object.keys(this.state.campaigns);
        const sortedCampaigns = campaignIds.map(id => this.state.campaigns[id])
            .sort((a, b) => (a.name || '').localeCompare(b.name));

        const selectedCampaignId = this.state.selectedCampaignId || sortedCampaigns[0].id;

        const adgroups = this.state.campaigns[selectedCampaignId].adgroups || {};
        const adgroupIds = Object.keys(adgroups).sort((aId, bId) => {
            const aName = adgroups[aId] || '';
            const bName = adgroups[bId];
            return aName.localeCompare(bName);
        });
        const selectAdgroupId = this.state.selectedAdgroupId || adgroupIds[0];

        const adGroupData = workorderData[selectAdgroupId].items;
        const summaryData = adgroupIds.map(adgroupId => ({
            id: adgroupId,
            ...workorderData[adgroupId].statistics
        })).sort((s1, s2) => s2.spend - s1.spend);
        const hasAdGroupDataSummaryData = summaryData.length > 0;


        const CampaignSelection = <RvDropdown labelName="Campaigns:"
            selectedId={selectedCampaignId} onChange={this.changeCampaign}>
            {
                sortedCampaigns.map(campaign =>
                    <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                )
            }
        </RvDropdown>;

        const AdgroupSelection = <RvDropdown labelName="Ad Groups:"
            selectedId={selectAdgroupId} onChange={this.changeAdgroup}>
            {
                adgroupIds.map(agId =>
                    <option key={agId} value={agId}>{adgroups[agId]}</option>
                )
            }
        </RvDropdown>;

        return (
            <Grid fluid>
                <ChannelTitle>{title}</ChannelTitle>

                {CampaignSelection}

                {hasAdGroupDataSummaryData ? (
                    <TwoAxisBarWithScatterChart
                        title="RV Partner Spend vs aCPA by Ad Group"
                        exportName="spend-vs-acpa-bar-and-scatter"
                        data={summaryData}
                        yBar="spend"
                        yBarTitle="RV Partner Spend"
                        scatter="acpa"
                        scatterTitle="aCPA"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yBarOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yBarUserOptions={{ formatByType: 'priceWithoutSpace' }}
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                        animateDuration={2000}
                        categories={summaryData.map(data => adgroups[data.id])}
                        help={
                            <React.Fragment>
                                <HelpTitle>RV Partner Spend vs aCPA by Ad Group</HelpTitle>
                                <HelpBody>
                                    <p>This graph shows the total partner cost for each adgroup
                                        during the period of time specified by the date picker as
                                        well as the aCPA (Attributed CPA). </p>
                                    <p>aCPA is Ration between Weighted Attributed Conversions and
                                        total partner spend as reported in the TTD impression and
                                        conversion logs for the period specified. </p>
                                    <p>Weighted Attributed Conversions is the fraction of conversion
                                        that can be attributed to the spend in a specific period,
                                        given a 30-day attribution window. For example, if we have
                                        specific TDID user that generated 100 conversions with 50
                                        impression during for the last 30 days, each impression will
                                        get an attributed conversion weight of 2. If we are looking
                                        at a day where 10 of those impressions were served, then the
                                        attributed conversions for that day will be 20 (10x2) </p>
                                    <p>This plot can be used to compare performance between adgroups
                                        for any specific date range, taking into account how the spend
                                        that happened in those days resulted in conversion in the
                                        30-day period after the impression happened. The insights
                                        from this view can be used to re-assign budgets to different
                                        adgroups.</p>
                                </HelpBody>
                            </React.Fragment>
                        }
                    />
                ) : (
                    <RenderingTile height='400px' show={hasAdGroupDataSummaryData} />
                )}

                {adGroupData ? (
                    <React.Fragment>

                        {AdgroupSelection}

                        <TwoAxisBarWithLineChart
                            exportName="rv-spend-&-acpa"
                            title={'RV Partner Spend & aCPA'}
                            data={adGroupData}
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yBar="spend"
                            yBarTitle="RV Partner Spend"
                            yBarOptions={{ formatByType: 'priceWithoutSpace' }}
                            yLine='acpa'
                            yLineTitle='aCPA'
                            yLineOptions={{ formatByType: 'priceWithoutSpace' }}
                            yAxisLineOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('priceWithoutSpace')
                                }
                            }}
                            yAxisBarOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('priceWithoutSpace')
                                }
                            }}
                            xAxisLabel='Date'
                            backfillBuckets={backfillBuckets}
                            animateDuration={2000}
                            xAxisType="datetime"
                            help={
                                <React.Fragment>
                                    <HelpTitle>RV Partner Spend &amp; aCPA</HelpTitle>
                                    <HelpBody>
                                        <p>This plot shows daily Spend and daily aCPA (attributed
                                            CPA) for the adgroup selected in the dropdown as reported
                                            in the Real Value Platform.  </p>
                                        <p>In this case, aCPA is Ration between Weighted Attributed
                                            Conversions and total partner spend as reported in the
                                            TTD impression and conversion logs for each day. </p>
                                        <p>Also, Weighted Attributed Conversions is the fraction of
                                            conversion that can be attributed to the spend on each
                                            day, given a 30-day attribution window. For example, if
                                            we have specific TDID user that generated 100 conversions
                                            with 50 impression during for the last 30 days, each
                                            impression will get an attributed conversion weight of
                                            2. If we are looking at a day where 10 of those impressions
                                            were served, then the attributed conversions for that
                                            day will be 20 (10x2) and the aCPA will be 20 divided by
                                            the partner cost on that day. </p>
                                        <p>This plot is a more granular and time series view of the
                                            “RV Partner Spend vs CPA by Ad Group” since it focusses on a
                                            single adgroup and allows the user to see how fluctuations
                                            in spend for different days have resulted in attributed
                                            conversions. This enables a trader to see how changes
                                            within an adgroups that occurred at different points in
                                            time have resulted in performance improvements. </p>
                                        <p></p>
                                    </HelpBody>
                                </React.Fragment>
                            }
                        />
                    </React.Fragment>
                ) : (
                    <RenderingTile height='400px' show={adGroupData || 'NO_CHART_DATA'} />
                )}


                {adGroupData ? (
                    <TwoAxisBarWithLineChart
                        exportName="rv-attributed-conversions"
                        title="RV attributed conversions"
                        data={adGroupData}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yBar="total_imps"
                        yBarTitle="Impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine={['attr_convs_weighted', 'attr_imps']}
                        yLineTitle={['Weighted Attributed Conversions', 'Attributed Impressions']}
                        yLineOptions={{ formatByType: 'floatX2' }}
                        xAxisLabel='Date'
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime"
                        help={
                            <React.Fragment>
                                <HelpTitle>RV Attributed Conversions</HelpTitle>
                                <HelpBody>
                                    <p>This plot shows the total number of impressions by day as well
                                        as the total number of attributed impressions and weighted
                                        attributed conversions. </p>
                                    <p>Weighted Attributed Conversions is the fraction of conversion
                                        that can be attributed to the spend on each day, given a
                                        30-day attribution window. For example, if we have specific
                                        TDID user that generated 100 conversions with 50 impression
                                        during for the last 30 days, each impression will get an
                                        attributed conversion weight of 2. If we are looking at a
                                        day where 10 of those impressions were served, then the a
                                        ttributed conversions for that day will be 20 (10x2) and the
                                        aCPA will be 20 divided by the partner cost on that day. </p>
                                    <p>Attributed impressions is the number of impressions that can
                                        be attributed to any conversion. All of these impressions
                                        will be associated with a user TDID that has resulted in a
                                        conversion within the 30-day period since the impression was
                                        delivered. </p>
                                    <p>This plot complements the view on “RV Partner Spend &amp; aCPA” view
                                        with another time series view that does not include a
                                        financial component. </p>
                                </HelpBody>
                            </React.Fragment>
                        }>
                    </TwoAxisBarWithLineChart>
                ) : null }

            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    realValue: state.realValue,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    layerTwo: state.layerTwo,
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchRealValue,
    setTimeRangeByKey
})(LabsRealValueContainer);



// WEBPACK FOOTER //
// ./src/containers/labs/LabsRealValueContainer.js